import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Edit2, Eye, EyeOff, Image, Video, ExternalLink, Save, X, Monitor, Smartphone, Layout, Smartphone as SmartphoneIcon, Shirt, Sofa, Home as HomeIcon, Car, BookOpen, Dumbbell, UtensilsCrossed, Leaf, Ticket, Wrench, Sparkles, ChevronDown, ChevronUp, Tag, Upload, Link, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Ad placement categories with visual representations
const adCategories = [
  {
    id: 'home',
    name: 'Home Page',
    icon: '🏠',
    placements: [
      { 
        id: 'home_top', 
        name: 'Top Banner', 
        description: 'Full width banner at the very top of home page',
        size: '1200x200',
        preview: 'w-full h-16 bg-gradient-to-r from-blue-500 to-purple-500'
      },
      { 
        id: 'home_middle', 
        name: 'Middle Section', 
        description: 'Banner between categories and listings',
        size: '1200x150',
        preview: 'w-full h-12 bg-gradient-to-r from-green-500 to-teal-500'
      },
      { 
        id: 'home_bottom', 
        name: 'Bottom Banner', 
        description: 'Banner at the bottom of home page',
        size: '1200x150',
        preview: 'w-full h-12 bg-gradient-to-r from-orange-500 to-red-500'
      },
    ]
  },
  {
    id: 'listing',
    name: 'Listing Pages',
    icon: '📦',
    placements: [
      { 
        id: 'listing_sidebar', 
        name: 'Sidebar Ad', 
        description: 'Vertical banner on listing detail sidebar',
        size: '300x600',
        preview: 'w-24 h-48 bg-gradient-to-b from-pink-500 to-rose-500'
      },
      { 
        id: 'listing_bottom', 
        name: 'Below Details', 
        description: 'Horizontal banner below listing details',
        size: '800x150',
        preview: 'w-full h-12 bg-gradient-to-r from-indigo-500 to-blue-500'
      },
    ]
  },
  {
    id: 'search',
    name: 'Search & Browse',
    icon: '🔍',
    placements: [
      { 
        id: 'search_top', 
        name: 'Search Results Top', 
        description: 'Banner above search results',
        size: '1200x100',
        preview: 'w-full h-10 bg-gradient-to-r from-amber-500 to-yellow-500'
      },
    ]
  }
];

const adTypes = [
  { id: 'banner', name: 'Image Banner', icon: Image },
  { id: 'video', name: 'Video Ad', icon: Video },
];

// Product listing categories (same as HomePage)
const productCategories = [
  { name: 'Electronics', Icon: SmartphoneIcon, gradient: '#3B82F6, #06B6D4' },
  { name: 'Fashion', Icon: Shirt, gradient: '#EC4899, #F43F5E' },
  { name: 'Furniture', Icon: Sofa, gradient: '#F59E0B, #EA580C' },
  { name: 'Home', Icon: HomeIcon, gradient: '#10B981, #14B8A6' },
  { name: 'Vehicles', Icon: Car, gradient: '#EF4444, #F97316' },
  { name: 'Books', Icon: BookOpen, gradient: '#8B5CF6, #A855F7' },
  { name: 'Sports', Icon: Dumbbell, gradient: '#22C55E, #84CC16' },
  { name: 'Food', Icon: UtensilsCrossed, gradient: '#F97316, #EF4444' },
  { name: 'Pets & Plants', Icon: Leaf, gradient: '#16A34A, #10B981' },
  { name: 'Coupons', Icon: Ticket, gradient: '#F59E0B, #EAB308' },
  { name: 'Services', Icon: Wrench, gradient: '#6366F1, #3B82F6' },
  { name: 'Other', Icon: Sparkles, gradient: '#64748B, #475569' },
];

export default function AdminAds() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPlacement, setSelectedPlacement] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [viewMode, setViewMode] = useState('categories'); // 'categories' or 'list'
  const [mediaInputMode, setMediaInputMode] = useState('upload'); // 'upload' or 'url'
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    type: 'banner',
    placement: 'home_top',
    media_url: '',
    link_url: '',
    is_active: true,
    start_date: '',
    end_date: '',
    priority: 1,
    categories: [],
  });

  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/admin/ads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAds(response.data);
    } catch (error) {
      toast.error('Failed to fetch ads');
    } finally {
      setLoading(false);
    }
  };

  const getAdsForPlacement = (placementId) => {
    return ads.filter(ad => ad.placement === placementId);
  };

  const handleCreateForPlacement = (placement) => {
    setSelectedPlacement(placement);
    resetForm();
    setFormData(prev => ({ ...prev, placement: placement.id }));
    setEditingAd(null);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      if (editingAd) {
        await axios.put(`${API_URL}/api/admin/ads/${editingAd.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Ad updated successfully');
      } else {
        await axios.post(`${API_URL}/api/admin/ads`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Ad created successfully');
      }
      setShowModal(false);
      setEditingAd(null);
      setSelectedPlacement(null);
      resetForm();
      fetchAds();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save ad');
    }
  };

  const handleEdit = (ad) => {
    setEditingAd(ad);
    setFormData({
      title: ad.title,
      type: ad.type,
      placement: ad.placement,
      media_url: ad.media_url,
      link_url: ad.link_url || '',
      is_active: ad.is_active,
      start_date: ad.start_date ? ad.start_date.split('T')[0] : '',
      end_date: ad.end_date ? ad.end_date.split('T')[0] : '',
      priority: ad.priority || 1,
      categories: ad.categories || [],
    });
    setMediaInputMode(ad.media_url ? 'url' : 'upload');
    setUploadedFileName('');
    setShowModal(true);
  };

  const handleDelete = async (adId) => {
    if (!window.confirm('Are you sure you want to delete this ad?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API_URL}/api/admin/ads/${adId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ad deleted successfully');
      fetchAds();
    } catch (error) {
      toast.error('Failed to delete ad');
    }
  };

  const toggleAdStatus = async (ad) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/api/admin/ads/${ad.id}`, {
        ...ad,
        is_active: !ad.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Ad ${ad.is_active ? 'deactivated' : 'activated'}`);
      fetchAds();
    } catch (error) {
      toast.error('Failed to update ad status');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      type: 'banner',
      placement: 'home_top',
      media_url: '',
      link_url: '',
      is_active: true,
      start_date: '',
      end_date: '',
      priority: 1,
      categories: [],
    });
    setUploadedFileName('');
    setUploadProgress(0);
    setMediaInputMode('upload');
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    const token = localStorage.getItem('admin_token');
    const isVideo = formData.type === 'video';
    const maxSize = isVideo ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
    const allowedTypes = isVideo
      ? ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime']
      : ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

    if (!allowedTypes.includes(file.type)) {
      toast.error(`Invalid file type. Allowed: ${allowedTypes.join(', ')}`);
      return;
    }
    if (file.size > maxSize) {
      toast.error(`File too large. Max ${isVideo ? '100MB' : '10MB'}`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const endpoint = isVideo ? '/api/upload/video' : '/api/upload/image';
      const response = await axios.post(`${API_URL}${endpoint}`, uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (e) => {
          const pct = Math.round((e.loaded * 100) / e.total);
          setUploadProgress(pct);
        },
      });
      const fileUrl = `${API_URL}${response.data.url}`;
      setFormData(prev => ({ ...prev, media_url: fileUrl }));
      setUploadedFileName(file.name);
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  const getPlacementInfo = (placementId) => {
    for (const cat of adCategories) {
      const placement = cat.placements.find(p => p.id === placementId);
      if (placement) return { ...placement, category: cat.name };
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Ad Management</h1>
          <p className="text-slate-600 mt-1">Create and manage ads, banners, and video placements</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('categories')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              viewMode === 'categories' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Layout className="w-4 h-4 inline mr-2" />
            By Placement
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              viewMode === 'list' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            All Ads
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <p className="text-3xl font-bold text-indigo-600">{ads.length}</p>
          <p className="text-sm text-slate-600">Total Ads</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <p className="text-3xl font-bold text-green-600">{ads.filter(a => a.is_active).length}</p>
          <p className="text-sm text-slate-600">Active</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <p className="text-3xl font-bold text-blue-600">{ads.filter(a => a.type === 'banner').length}</p>
          <p className="text-sm text-slate-600">Banners</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <p className="text-3xl font-bold text-purple-600">{ads.filter(a => a.type === 'video').length}</p>
          <p className="text-sm text-slate-600">Videos</p>
        </div>
      </div>

      {viewMode === 'categories' ? (
        /* Category View */
        <div className="space-y-6">
          {adCategories.map((category) => (
            <div key={category.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              {/* Category Header */}
              <div className="p-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <span className="text-2xl">{category.icon}</span>
                  {category.name}
                </h2>
              </div>

              {/* Placements */}
              <div className="divide-y divide-slate-100">
                {category.placements.map((placement) => {
                  const placementAds = getAdsForPlacement(placement.id);
                  return (
                    <div key={placement.id} className="p-4">
                      <div className="flex items-start gap-4">
                        {/* Visual Preview */}
                        <div className="flex-shrink-0">
                          <div className={`${placement.preview} rounded-lg shadow-inner flex items-center justify-center text-white text-xs font-medium`}>
                            {placement.size}
                          </div>
                        </div>

                        {/* Placement Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <h3 className="font-semibold text-slate-900">{placement.name}</h3>
                              <p className="text-sm text-slate-500">{placement.description}</p>
                              <p className="text-xs text-slate-400 mt-1">Recommended size: {placement.size}px</p>
                            </div>
                            <button
                              onClick={() => handleCreateForPlacement(placement)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
                            >
                              <Plus className="w-4 h-4" />
                              Add Ad
                            </button>
                          </div>

                          {/* Ads for this placement */}
                          {placementAds.length > 0 ? (
                            <div className="mt-3 space-y-2">
                              {placementAds.map((ad) => (
                                <div key={ad.id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                                  {/* Thumbnail */}
                                  <div className="w-16 h-10 bg-slate-200 rounded overflow-hidden flex-shrink-0">
                                    {ad.type === 'video' ? (
                                      <div className="w-full h-full flex items-center justify-center bg-slate-300">
                                        <Video className="w-5 h-5 text-slate-500" />
                                      </div>
                                    ) : (
                                      <img 
                                        src={ad.media_url} 
                                        alt={ad.title}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    )}
                                  </div>

                                  {/* Ad Info */}
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-slate-900 truncate text-sm">{ad.title}</p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                        ad.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                                      }`}>
                                        {ad.is_active ? 'Active' : 'Inactive'}
                                      </span>
                                      <span className="text-xs text-slate-500">
                                        {ad.impressions || 0} views • {ad.clicks || 0} clicks
                                      </span>
                                    </div>
                                    {ad.categories && ad.categories.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {ad.categories.slice(0, 3).map(c => (
                                          <span key={c} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-600">{c}</span>
                                        ))}
                                        {ad.categories.length > 3 && (
                                          <span className="text-[10px] text-slate-400">+{ad.categories.length - 3} more</span>
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  {/* Actions */}
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => toggleAdStatus(ad)}
                                      className={`p-1.5 rounded transition-colors ${
                                        ad.is_active 
                                          ? 'text-amber-600 hover:bg-amber-100' 
                                          : 'text-green-600 hover:bg-green-100'
                                      }`}
                                      title={ad.is_active ? 'Deactivate' : 'Activate'}
                                    >
                                      {ad.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                    <button
                                      onClick={() => handleEdit(ad)}
                                      className="p-1.5 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                                      title="Edit"
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDelete(ad.id)}
                                      className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors"
                                      title="Delete"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-slate-400 mt-2 italic">No ads in this placement</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">All Ads</h2>
            <button
              onClick={() => {
                resetForm();
                setEditingAd(null);
                setShowModal(true);
              }}
              className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Ad
            </button>
          </div>
          
          {ads.length === 0 ? (
            <div className="p-8 text-center">
              <Image className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">No ads created yet</p>
              <p className="text-sm text-slate-500 mt-1">Click "Create Ad" or select a placement above</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {ads.map((ad) => {
                const placementInfo = getPlacementInfo(ad.placement);
                return (
                  <div key={ad.id} className="p-4 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-4">
                      {/* Preview */}
                      <div className="w-24 h-16 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                        {ad.type === 'video' ? (
                          <div className="w-full h-full flex items-center justify-center bg-slate-200">
                            <Video className="w-8 h-8 text-slate-400" />
                          </div>
                        ) : (
                          <img 
                            src={ad.media_url} 
                            alt={ad.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="96" height="64" fill="%23e2e8f0"><rect width="96" height="64"/><text x="48" y="32" text-anchor="middle" fill="%2394a3b8" font-size="10">No Image</text></svg>';
                            }}
                          />
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-900 truncate">{ad.title}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            ad.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {ad.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600">{placementInfo?.category} → {placementInfo?.name}</p>
                        <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                          <span>{ad.impressions || 0} impressions</span>
                          <span>{ad.clicks || 0} clicks</span>
                          {ad.impressions > 0 && (
                            <span>CTR: {((ad.clicks / ad.impressions) * 100).toFixed(1)}%</span>
                          )}
                        </div>
                        {ad.categories && ad.categories.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {ad.categories.map(c => {
                              const catInfo = productCategories.find(pc => pc.name === c);
                              return (
                                <span key={c} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">
                                  {catInfo && <catInfo.Icon className="w-3 h-3" />}
                                  {c}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleAdStatus(ad)}
                          className={`p-2 rounded-lg transition-colors ${
                            ad.is_active 
                              ? 'bg-amber-100 text-amber-600 hover:bg-amber-200' 
                              : 'bg-green-100 text-green-600 hover:bg-green-200'
                          }`}
                          title={ad.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {ad.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleEdit(ad)}
                          className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(ad.id)}
                          className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  {editingAd ? 'Edit Ad' : 'Create New Ad'}
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingAd(null);
                  setSelectedPlacement(null);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Ad Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Enter ad title"
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Ad Type</label>
                <div className="grid grid-cols-2 gap-3">
                  {adTypes.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, type: type.id })}
                      className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                        formData.type === type.id
                          ? 'border-indigo-600 bg-indigo-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <type.icon className={`w-5 h-5 ${formData.type === type.id ? 'text-indigo-600' : 'text-slate-400'}`} />
                      <span className={`font-medium ${formData.type === type.id ? 'text-indigo-600' : 'text-slate-700'}`}>
                        {type.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Placement Category Dropdown */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Select Placement *</label>
                
                {/* Category Selection */}
                <div className="space-y-3">
                  {adCategories.map((cat) => (
                    <div key={cat.id} className="border border-slate-200 rounded-lg overflow-hidden">
                      {/* Category Header */}
                      <div className="bg-slate-50 px-3 py-2 flex items-center gap-2 border-b border-slate-200">
                        <span className="text-lg">{cat.icon}</span>
                        <span className="font-medium text-slate-700">{cat.name}</span>
                      </div>
                      
                      {/* Placements */}
                      <div className="p-2 grid grid-cols-1 gap-2">
                        {cat.placements.map((placement) => (
                          <button
                            key={placement.id}
                            type="button"
                            data-testid={`placement-${placement.id}`}
                            onClick={() => setFormData({ ...formData, placement: placement.id })}
                            className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                              formData.placement === placement.id
                                ? 'border-indigo-600 bg-indigo-50'
                                : 'border-slate-100 hover:border-slate-300 bg-white'
                            }`}
                          >
                            {/* Preview Box */}
                            <div className={`flex-shrink-0 ${placement.id.includes('sidebar') ? 'w-6 h-12' : 'w-16 h-6'} rounded ${
                              formData.placement === placement.id
                                ? 'bg-indigo-500'
                                : 'bg-slate-300'
                            }`}></div>
                            
                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium text-sm ${
                                formData.placement === placement.id ? 'text-indigo-700' : 'text-slate-900'
                              }`}>
                                {placement.name}
                              </p>
                              <p className="text-xs text-slate-500 truncate">{placement.description}</p>
                              <p className="text-xs text-slate-400 mt-0.5">Size: {placement.size}px</p>
                            </div>
                            
                            {/* Selected Indicator */}
                            {formData.placement === placement.id && (
                              <div className="w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center">
                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Target Product Categories */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <Tag className="w-4 h-4 inline mr-1" />
                  Target Categories
                  <span className="text-xs text-slate-400 ml-1">(optional — leave empty for all)</span>
                </label>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {productCategories.map((cat) => {
                    const isSelected = formData.categories.includes(cat.name);
                    return (
                      <button
                        key={cat.name}
                        type="button"
                        data-testid={`ad-category-${cat.name}`}
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            categories: isSelected
                              ? prev.categories.filter(c => c !== cat.name)
                              : [...prev.categories, cat.name]
                          }));
                        }}
                        className={`flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all ${
                          isSelected
                            ? 'border-indigo-600 bg-indigo-50 shadow-sm'
                            : 'border-slate-100 hover:border-slate-300 bg-white'
                        }`}
                      >
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ background: `linear-gradient(135deg, ${cat.gradient})` }}
                        >
                          <cat.Icon className="w-4 h-4 text-white" />
                        </div>
                        <span className={`text-[10px] font-medium leading-tight text-center ${
                          isSelected ? 'text-indigo-700' : 'text-slate-600'
                        }`}>
                          {cat.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {formData.categories.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {formData.categories.map(c => (
                      <span key={c} className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full font-medium">
                        {c}
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            categories: prev.categories.filter(cat => cat !== c)
                          }))}
                          className="hover:text-indigo-900"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, categories: [] }))}
                      className="text-xs text-slate-500 hover:text-slate-700 underline ml-1"
                    >
                      Clear all
                    </button>
                  </div>
                )}
              </div>

              {/* Media Upload / URL */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-slate-700">
                    {formData.type === 'video' ? 'Video *' : 'Banner Image *'}
                  </label>
                  <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
                    <button
                      type="button"
                      data-testid="media-mode-upload"
                      onClick={() => setMediaInputMode('upload')}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                        mediaInputMode === 'upload' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500'
                      }`}
                    >
                      <Upload className="w-3 h-3" /> Upload
                    </button>
                    <button
                      type="button"
                      data-testid="media-mode-url"
                      onClick={() => setMediaInputMode('url')}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                        mediaInputMode === 'url' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500'
                      }`}
                    >
                      <Link className="w-3 h-3" /> URL
                    </button>
                  </div>
                </div>

                {mediaInputMode === 'upload' ? (
                  <div>
                    {/* Drop zone */}
                    <div
                      data-testid="upload-dropzone"
                      onDrop={handleDrop}
                      onDragOver={(e) => e.preventDefault()}
                      onClick={() => !uploading && fileInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-all ${
                        uploading ? 'border-indigo-400 bg-indigo-50' :
                        formData.media_url && uploadedFileName ? 'border-green-400 bg-green-50' :
                        'border-slate-300 hover:border-indigo-400 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={formData.type === 'video' ? 'video/mp4,video/webm,video/ogg' : 'image/jpeg,image/png,image/gif,image/webp'}
                        onChange={(e) => handleFileUpload(e.target.files[0])}
                        className="hidden"
                        data-testid="file-input"
                      />

                      {uploading ? (
                        <div className="space-y-2">
                          <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mx-auto" />
                          <p className="text-sm font-medium text-indigo-600">Uploading... {uploadProgress}%</p>
                          <div className="w-full bg-slate-200 rounded-full h-2 max-w-xs mx-auto">
                            <div className="bg-indigo-600 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }}></div>
                          </div>
                        </div>
                      ) : formData.media_url && uploadedFileName ? (
                        <div className="space-y-2">
                          <CheckCircle className="w-8 h-8 text-green-500 mx-auto" />
                          <p className="text-sm font-medium text-green-700">Uploaded: {uploadedFileName}</p>
                          <p className="text-xs text-slate-500">Click or drop to replace</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Upload className="w-8 h-8 text-slate-400 mx-auto" />
                          <p className="text-sm font-medium text-slate-700">
                            {formData.type === 'video' ? 'Drop video here or click to browse' : 'Drop image here or click to browse'}
                          </p>
                          <p className="text-xs text-slate-400">
                            {formData.type === 'video' ? 'MP4, WebM, OGG — Max 100MB' : 'JPG, PNG, GIF, WebP — Max 10MB'}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Show current URL if set from a previous upload or edit */}
                    {formData.media_url && !uploadedFileName && (
                      <p className="text-xs text-slate-500 mt-1 truncate">Current: {formData.media_url}</p>
                    )}
                  </div>
                ) : (
                  <input
                    type="url"
                    data-testid="media-url-input"
                    value={formData.media_url}
                    onChange={(e) => setFormData({ ...formData, media_url: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder={formData.type === 'video' ? 'https://youtube.com/...' : 'https://example.com/image.jpg'}
                  />
                )}
              </div>

              {/* Link URL */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Click-through URL
                </label>
                <input
                  type="url"
                  value={formData.link_url}
                  onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="https://example.com"
                />
              </div>

              {/* Priority & Status Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-5 h-5 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                    />
                    <span className="font-medium text-slate-700">Active</span>
                  </label>
                </div>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Preview */}
              {formData.media_url && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Preview</label>
                  <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
                    {formData.type === 'video' ? (
                      <video
                        src={formData.media_url}
                        controls
                        className="w-full h-40 object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<p class="p-4 text-center text-slate-500 text-sm">Unable to load video preview</p>';
                        }}
                      />
                    ) : (
                      <img 
                        src={formData.media_url} 
                        alt="Preview"
                        className="w-full h-40 object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<p class="p-4 text-center text-slate-500 text-sm">Unable to load image preview</p>';
                        }}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Submit */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingAd(null);
                    setSelectedPlacement(null);
                  }}
                  className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading || !formData.media_url}
                  className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4" />
                  {editingAd ? 'Update Ad' : 'Create Ad'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
